/* eslint-env browser */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Editor as DraftEditor,
  EditorState,
  ContentState,
  RichUtils,
  CompositeDecorator,
  Modifier,
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import getFragmentFromSelection from 'draft-js/lib/getFragmentFromSelection';
import 'draft-js/dist/Draft.css';
import CCEvents, { EventNames } from '@companycam/companycam-events';

import SnippetsLibraryModal from 'components/shared/SnippetsLibrary';

import Toolbar from './Toolbar';
import { getTextFromHTML, sanitizeUrl } from './utils';

function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === 'LINK'
    );
  }, callback);
}

const Link = (props) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  const sanitizedUrl = sanitizeUrl(url);

  return (
    <a href={sanitizedUrl} style={{ cursor: 'pointer' }}>
      {props.children}
    </a>
  );
};

Link.propTypes = {
  contentState: PropTypes.object,
  entityKey: PropTypes.string,
  children: PropTypes.node,
};

const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

const Editor = ({
  initialContents,
  placeholder,
  onChange,
  onBlur,
  autoFocus,
  snippetsVisible,
  snippetsEnabled,
  disabled,
  contentChanging,
  tabIndex,
  showsToolbarOnlyOnFocus = false,
}) => {
  const editorRef = useRef();
  const urlInputRef = useRef();

  const initialState = stateFromHTML(initialContents || '');

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(initialState, decorator),
  );

  const [cursorPosition, setCursorPosition] = useState('');
  const [isEditorCloseable, setEditorCloseable] = useState(true);
  const [showSnippetModal, setShowSnippetModal] = useState(false);
  const [selectedText, setSelectedText] = useState(null);

  const getSelectedText = () => {
    const selected = getFragmentFromSelection(editorState);
    const blocks = selected ? selected.toArray() : [];
    const contentState = ContentState.createFromBlockArray(blocks);
    return stateToHTML(contentState);
  };

  useEffect(() => {
    if (editorRef.current && autoFocus) {
      editorRef.current.focus();
    }
  }, [editorRef, autoFocus]);

  useEffect(() => {
    const editorIsFocused =
      document.activeElement === editorRef?.current?.editor ||
      urlInputRef?.current;

    if (contentChanging && !editorIsFocused) {
      const reinitializedState = stateFromHTML(initialContents || '');

      setEditorState(
        EditorState.createWithContent(reinitializedState, decorator),
      );
    }
  }, [initialContents, contentChanging]);

  const onEditorStateChange = (newState) => {
    setEditorState(newState);
    onChange(editorState, newState);
  };

  const onEditorBlur = () => {
    onBlur(editorState, isEditorCloseable);
  };

  const insertSnippet = (snippetText) => {
    let selectionState = editorState.getSelection();
    const currentContent = editorState.getCurrentContent();

    const snippetState = stateFromHTML(snippetText);

    const nextContent = Modifier.replaceWithFragment(
      currentContent,
      selectionState,
      snippetState.getBlockMap(),
    );

    let nextEditorState = EditorState.push(
      editorState,
      nextContent,
      'insert-fragment',
    );

    const blockArray = snippetState.getBlocksAsArray();

    // If inserted snippet is just one block of <h1>, then editor needs to be toggled to H1
    // If inserted snippet is multiple blocks, then style does not need to be toggled
    if (
      snippetText.substring(0, 4) === '<h1>' &&
      snippetText.slice(-5) === '</h1>' &&
      blockArray.length === 1
    ) {
      nextEditorState = RichUtils.toggleBlockType(
        nextEditorState,
        'header-one',
      );
    }

    const plainText = getTextFromHTML(snippetText);

    // Move cursor to end of inserted text
    selectionState = selectionState.merge({
      anchorOffset: selectionState.getAnchorOffset() + plainText.length,
      focusOffset: selectionState.getAnchorOffset() + plainText.length,
    });

    nextEditorState = EditorState.forceSelection(
      nextEditorState,
      selectionState,
    );

    onEditorStateChange(nextEditorState);
    setSelectedText(null);
    setShowSnippetModal(false);
    setEditorCloseable(true);
    CCEvents.logEvent(EventNames.REPORT_INSERT_SNIPPET);
  };

  const handleKeyCommand = (command, innerEditorState) => {
    const newState = RichUtils.handleKeyCommand(innerEditorState, command);

    if (newState) {
      onEditorStateChange(newState);
      return true;
    }
    return false;
  };

  const handleAddSnippet = () => {
    let selectedSnippetText = getSelectedText();
    if (!selectedSnippetText) {
      selectedSnippetText = stateToHTML(editorState.getCurrentContent());
    }

    setSelectedText(selectedSnippetText);
    setEditorCloseable(false);
    setShowSnippetModal(true);
    setCursorPosition(editorState.getSelection());
  };

  const handleOpenSnippetModal = () => {
    // For clicking snippets for the first time
    localStorage.setItem('snippetsClicked', 'true');
    setEditorCloseable(false);
    setShowSnippetModal(true);
    setCursorPosition(editorState.getSelection());
  };

  const closeSnippetModal = () => {
    setSelectedText(null);
    setShowSnippetModal(false);
    setEditorCloseable(true);
    // Move cursor to previous position
    setEditorState(EditorState.forceSelection(editorState, cursorPosition));
  };

  const isFocused =
    document.activeElement === editorRef?.current?.editor ||
    urlInputRef?.current;

  return (
    <>
      <div className="RichEditor-root">
        <div className="RichEditor-editor">
          <DraftEditor
            ref={editorRef}
            editorState={editorState}
            onChange={onEditorStateChange}
            onBlur={onEditorBlur}
            handleKeyCommand={handleKeyCommand}
            spellCheck
            readOnly={disabled}
            tabIndex={tabIndex || 0}
            placeholder={placeholder}
          />
        </div>
        {(showsToolbarOnlyOnFocus && isFocused) || !showsToolbarOnlyOnFocus ? (
          <Toolbar
            editorState={editorState}
            onStyleToggle={onEditorStateChange}
            setEditorCloseable={setEditorCloseable}
            handleAddSnippet={handleAddSnippet}
            handleOpenSnippetModal={handleOpenSnippetModal}
            snippetsEnabled={snippetsEnabled}
            snippetsVisible={snippetsVisible}
            urlInputRef={urlInputRef}
          />
        ) : null}
      </div>

      <SnippetsLibraryModal
        isOpen={showSnippetModal ? snippetsEnabled : null}
        onRequestClose={closeSnippetModal}
        selectedText={selectedText}
        insertSnippet={insertSnippet}
        setSelectedText={setSelectedText}
      />
    </>
  );
};

Editor.propTypes = {
  initialContents: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  autoFocus: PropTypes.bool,
  snippetsVisible: PropTypes.bool,
  snippetsEnabled: PropTypes.bool,
  disabled: PropTypes.bool,
  contentChanging: PropTypes.bool,
  tabIndex: PropTypes.number,
  showsToolbarOnlyOnFocus: PropTypes.bool,
};

Editor.defaultProps = {
  onBlur: () => {},
};

export default Editor;
